/**
 * Entrar — `OD-0026` (login y CSRF).
 *
 * `OD-0017 D-04`: etiquetas reales, errores asociados al campo y anunciados,
 * y todo alcanzable por teclado. Sin conexión no se promete nada: el botón se
 * deshabilita y se dice por qué (`ADR-0004 X2`, mismo criterio).
 */
import { useState } from "react";
import { api } from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo, Tarjeta } from "../ui/primitivas";

export function PantallaLogin({
  hayConexion,
  exito = null,
  onAutenticado,
  onMfa,
  onRegistro,
  onRecuperar,
  onReenviarEnlace,
}: {
  hayConexion: boolean;
  /** Lo que acaba de terminar bien en otra pantalla (email verificado, contraseña cambiada). */
  exito?: string | null;
  onAutenticado: () => void;
  onMfa: (challenge: string) => void;
  onRegistro: () => void;
  onRecuperar: () => void;
  /** «Reenviar enlace»: volver a pedir el de confirmación, con el email ya escrito. */
  onReenviarEnlace: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [errorPassword, setErrorPassword] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  /** Tras un «incorrectos»: puede ser una cuenta sin confirmar. No se sabe, y no se dice. */
  const [quizaSinConfirmar, setQuizaSinConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrorGeneral(null);
    setQuizaSinConfirmar(false);
    setErrorEmail(email.trim() === "" ? t.login.faltaEmail : null);
    setErrorPassword(password === "" ? t.login.faltaPassword : null);
    if (email.trim() === "" || password === "") return;

    setEnviando(true);
    const r = await api.login(email.trim(), password);
    setEnviando(false);

    if (r.ok) {
      setPassword("");
      if (r.datos.tipo === "AUTENTICADO") onAutenticado();
      else onMfa(r.datos.challenge);
      return;
    }

    // El mismo mensaje para email inexistente y contraseña incorrecta: la API
    // ya responde igual en ambos casos (OD-0026) y la interfaz no lo delata.
    if (r.estado === 0) setErrorGeneral(t.login.sinRed);
    else if (r.estado === 401) {
      setErrorGeneral(t.login.credencialesIncorrectas);
      // Hallazgo 6: una cuenta sin confirmar no existe todavía (la crea la
      // verificación del email), así que la API responde lo mismo que a un
      // email inventado. Se ofrece el enlace a TODO 401: decirlo sólo a las
      // pendientes contaría qué emails tienen cuenta.
      setQuizaSinConfirmar(true);
    }
    else if (r.estado === 429) setErrorGeneral(t.login.demasiadosIntentos);
    else setErrorGeneral(t.login.errorGenerico);
  }

  return (
    <Tarjeta titulo={t.login.titulo}>
      {exito !== null ? <Aviso tono="exito">{exito}</Aviso> : null}
      <form onSubmit={enviar} noValidate>
        <Campo
          id="email"
          etiqueta={t.login.email}
          valor={email}
          onChange={(v) => {
            setEmail(v);
            setErrorEmail(null);
          }}
          tipo="email"
          inputMode="email"
          autoComplete="username"
          error={errorEmail}
          autoFocus
        />
        <Campo
          id="password"
          etiqueta={t.login.password}
          valor={password}
          onChange={(v) => {
            setPassword(v);
            setErrorPassword(null);
          }}
          tipo="password"
          mostrar={t.verContrasena}
          autoComplete="current-password"
          error={errorPassword}
        />
        {errorGeneral !== null ? <Aviso tono="error">{errorGeneral}</Aviso> : null}
        {quizaSinConfirmar ? (
          <Aviso tono="info" titulo={t.login.sinConfirmarTitulo}>
            {t.login.sinConfirmarDetalle}{" "}
            <Boton variante="terciario" onClick={() => onReenviarEnlace(email.trim())}>
              {t.login.reenviarEnlace}
            </Boton>
          </Aviso>
        ) : null}
        {!hayConexion ? <Aviso tono="aviso">{t.login.sinRed}</Aviso> : null}
        <Boton tipo="submit" deshabilitado={enviando || !hayConexion}>
          {enviando ? t.login.entrando : t.login.entrar}
        </Boton>
      </form>
      {/*
        DOS ACCIONES QUE NO PESAN LO MISMO, y hasta el 2026-10-15 eran dos
        botones IDENTICOS: recuperar la contraseña es una salida de emergencia,
        y crear una cuenta es la puerta del producto. Con el mismo peso no hay
        jerarquia, y el ojo no sabe cual es cual.

        No hace falta inventar nada: `OD-0017` D-01 ya define `terciario` como
        "se lee como un enlace, pero hace algo aqui", que es exactamente lo que
        es recuperar. Asi «Crear una cuenta» queda como el unico con borde de
        esta fila.
      */}
      <div className="acciones">
        <Boton variante="terciario" onClick={onRecuperar}>
          {t.login.olvidePassword}
        </Boton>
        <Boton variante="secundario" onClick={onRegistro}>
          {t.login.crearCuenta}
        </Boton>
      </div>
    </Tarjeta>
  );
}
